/**
 * Editor state and actions.
 *
 * Everything the editor knows lives in one plain object, and every change is a
 * reducer action (design §1.7). The React layer dispatches and renders; it holds
 * no editing logic of its own. That is what lets "dragging from valve_1.port_a to
 * chamber_1.port_b creates one edge" be a unit test rather than a synthesised
 * pointer sequence against SVG.
 *
 * The document is the working draft. It is replaced, never mutated, and only
 * the elements an edit touches get new objects — which is what the scene
 * builder's per-element memo relies on (requirement 14.4).
 */
import { servesKind, type DesignDocument, type DesignKind, type LabelSide } from "../design/document";
import type { ClipboardContent } from "./clipboard";
import { NO_DEFAULTS, type PlacementDefaults } from "./defaults";
import { DEFAULT_VIEWPORT, type Viewport } from "../render";
import {
  ALL_ANNOTATIONS,
  type Bounds,
  type Point,
  type SceneAnnotations,
  type SceneLibrary,
  type Wash,
} from "../scene/types";

/** `note`: a click writes a note there (on a component, one that moves with it). */
export type Tool = "select" | "connect" | "note";

export interface GridSettings {
  spacing: number;
  /** On by default (D21). */
  snap: boolean;
  visible: boolean;
}

export type RefusalCode =
  | "unknown_port"
  | "same_node"
  | "port_occupied"
  | "dead_end_port"
  | "unexposed_block_port"
  | "locked_block_member"
  | "unknown_template"
  | "not_a_node_template"
  | "no_connector_template"
  | "unknown_element"
  | "unknown_parameter"
  | "no_junction_template"
  | "incompatible_template"
  | "mounted_port"
  | "cannot_mount";

export interface Refusal {
  code: RefusalCode;
  message: string;
  /** The elements the refusal is about, for highlighting or a detach offer. */
  refs: string[];
}

/**
 * Something the designer should be told about the last action. A refusal says
 * why nothing happened; a removal says what went with a deletion, so that
 * cascading deletes are never silent (requirement 6.6).
 */
export type Notice =
  | ({ kind: "refused" } & Refusal)
  /**
   * `orphanedApplications`: treatments, reagent deposits and thermal zones left
   * targeting something the deletion removed (D27).
   */
  | { kind: "removed"; message: string; refs: string[]; orphanedApplications: string[] }
  /** Something happened that the designer should know about, but nothing was refused. */
  | { kind: "info"; message: string; refs: string[] };

export type Drag =
  | {
      kind: "move";
      /** The document when the drag began; offsets apply to it, not cumulatively. */
      origin: DesignDocument;
    }
  | {
      kind: "connect";
      from: string;
      hover: string | null;
      /** Every port a connection from `from` may end on, for highlighting (D21). */
      validTargets: ReadonlySet<string>;
    };

export interface EditorState {
  document: DesignDocument;
  /**
   * Microfluidic device or tubing set (D34). Decides which templates the
   * editor picks by itself: the connector, the junction, the air line.
   */
  designKind: DesignKind;
  /** The draft revision the working document was based on, for the save's check. */
  revision: number | null;
  /**
   * Identifiers the published base version uses and the draft does not (D29).
   * Not retired until the next publish, so an undo may restore one, but never
   * allocated to a new element.
   */
  reserved: ReadonlySet<string>;
  /** What the server holds at `revision`. Unsaved work is `document !== savedDocument`. */
  savedDocument: DesignDocument;
  library: SceneLibrary;
  /** Selected node, edge and block instance refs. */
  selection: ReadonlySet<string>;
  tool: Tool;
  /** The edge template the connector tool creates channels from. */
  connectorTemplateId: string | null;
  /** The node template a branch off a line is made with: a T junction by default. */
  junctionTemplateId: string | null;
  /** The edge template for air lines, chosen for lines to gas ports (D30). */
  airLineTemplateId: string | null;
  /**
   * Template version id -> template entity id, for placement defaults, which
   * are kept by entity (D36). Filled in as the library loads.
   */
  templateEntities: Readonly<Record<string, string>>;
  /**
   * This person's placement defaults (D36), from the browser: used where the
   * design sets none. Never undone, never saved to the design.
   */
  userDefaults: PlacementDefaults;
  drag: Drag | null;
  grid: GridSettings;
  /** Block instances drawn collapsed. View state: never undone, never saved. */
  collapsed: ReadonlySet<string>;
  /** What Copy or Cut last took. Not undone, not saved: like any clipboard. */
  clipboard: ClipboardContent | null;
  /** Labels, dimensions and each kind of line shown or hidden. View state: never undone, never saved. */
  annotations: SceneAnnotations;
  /** Pan and zoom. View state, like `collapsed`: never undone, never saved. */
  viewport: Viewport;
  /** Past documents, most recent last. Snapshots rather than inverse commands. */
  undo: readonly DesignDocument[];
  redo: readonly DesignDocument[];
  notice: Notice | null;
}

export type SelectMode = "replace" | "extend" | "toggle";

/** A note card's width and height, in scene units. */
export interface NoteSizeValue {
  width: number;
  height: number;
}

export type EditorAction =
  | { type: "DRAFT_LOADED"; document: DesignDocument; revision: number; reserved?: readonly string[] }
  /**
   * `document` is what was sent. `retired` is the saved draft's retired list,
   * which the server extends with every identifier the save removed.
   */
  | {
      type: "DRAFT_SAVED";
      document: DesignDocument;
      revision: number;
      retired?: readonly string[];
      reserved?: readonly string[];
    }
  /**
   * Adopt a draft someone else saved, after a conflict. Unlike DRAFT_LOADED,
   * the local work is not discarded: it becomes an undo step, so the designer
   * can step back to it (requirement 13.6).
   */
  | { type: "DRAFT_ADOPTED"; document: DesignDocument; revision: number; reserved?: readonly string[] }
  | { type: "LIBRARY_LOADED"; library: SceneLibrary }
  /**
   * The design changed kind (D34). Its own picks (connector, junction, air
   * line) move to the new kind's templates; the document is untouched.
   */
  | { type: "SET_DESIGN_KIND"; kind: DesignKind }
  | { type: "SET_TOOL"; tool: Tool }
  /** Close the notice about the last action: dismissed, or timed out. */
  | { type: "CLEAR_NOTICE" }
  | { type: "SET_CONNECTOR_TEMPLATE"; templateVersionId: string }
  | { type: "SET_JUNCTION_TEMPLATE"; templateVersionId: string }
  | { type: "SET_AIR_LINE_TEMPLATE"; templateVersionId: string }
  | { type: "SET_TEMPLATE_ENTITIES"; entities: Readonly<Record<string, string>> }
  /** The design's placement defaults (D36): an edit of the document, so one undo step. */
  | { type: "SET_PLACEMENT_DEFAULTS"; defaults: PlacementDefaults }
  /** This person's placement defaults (D36): view-like state, not an edit of the design. */
  | { type: "SET_USER_DEFAULTS"; defaults: PlacementDefaults }
  /**
   * Branch off a line: splice a junction into it at `at` (its label point
   * when omitted) and, with `from`, connect that port to the junction's free
   * port. A port takes one line, so a common rail is built from junctions;
   * this is the one-step way to do it. One undo step.
   */
  | { type: "BRANCH_EDGE"; edge: string; from?: string; at?: Point }
  /**
   * Place a component. With `intoEdge`, also splice it into that line: the line
   * is split at the component, each part attached to one of its fluidic ports.
   */
  | { type: "PLACE_NODE"; templateVersionId: string; at: Point; intoEdge?: string }
  | { type: "BEGIN_CONNECT"; port: string }
  | { type: "HOVER_PORT"; port: string | null }
  /** Ends on `port`, or on the hovered port when omitted. Neither cancels. */
  | { type: "COMPLETE_CONNECT"; port?: string }
  /** Abandons any drag, restoring the document a move began from. */
  | { type: "CANCEL" }
  | { type: "SELECT"; refs: readonly string[]; mode?: SelectMode }
  | { type: "SELECT_ALL" }
  | { type: "CLEAR_SELECTION" }
  | { type: "BOX_SELECT"; box: Bounds; mode?: "replace" | "extend" }
  | { type: "BEGIN_MOVE" }
  /**
   * During a move drag: the total offset since BEGIN_MOVE. Outside one: a
   * nudge, committed as its own undo step (the keyboard path).
   */
  | { type: "MOVE_SELECTION"; dx: number; dy: number }
  /**
   * Ends a move drag. With `intoEdge`, the one component moved is spliced
   * into that line at `at`, as a palette drop does; the move and the splice
   * are one undo step.
   */
  | { type: "END_MOVE"; intoEdge?: string; at?: Point }
  /**
   * Quarter turns, positive clockwise. One component turns in place; several,
   * or a block, turn about the group's centre (editor/orient.ts). Presentation
   * only, so permitted on locked block members (D20).
   */
  | { type: "ROTATE_SELECTION"; quarterTurns: number }
  /** Flip in screen terms, with the same grouping rules as rotation. */
  | { type: "MIRROR_SELECTION"; axis: "horizontal" | "vertical" }
  | { type: "DELETE_SELECTION" }
  /** `value` is canonical SI; null clears the override back to the default. */
  | { type: "EDIT_PARAMETER"; ref: string; key: string; value: unknown }
  /** One parameter on several elements at once: a multi-selection edited in the inspector. */
  | { type: "EDIT_PARAMETERS"; refs: readonly string[]; key: string; value: unknown }
  | { type: "SET_GRID"; spacing?: number; snap?: boolean; visible?: boolean }
  | { type: "SET_COLLAPSED"; instance: string; collapsed: boolean }
  /** Where a collapsed block's symbol sits; null returns it to being placed from its lines. */
  | { type: "SET_COLLAPSED_POSITION"; instance: string; position: Point | null }
  /** Switching a kind of line off also deselects any line of that kind. */
  | {
      type: "SET_ANNOTATIONS";
      labels?: boolean;
      dimensions?: boolean;
      fluidLines?: boolean;
      airLines?: boolean;
      notes?: boolean;
      wash?: Wash;
      /** Show only the washed side (SceneAnnotations.isolate). */
      isolate?: boolean;
    }
  | { type: "SET_VIEWPORT"; viewport: Viewport }
  /**
   * Swap a channel between edge templates (straight and mixing), keeping its
   * length, width and depth: the designer is changing what it is, not its size.
   */
  | { type: "SET_EDGE_TEMPLATE"; ref: string; templateVersionId: string }
  /**
   * Where an edge's bends sit (D23); an empty list returns it to automatic
   * routing. Presentation only, so permitted on locked block members. During a
   * move drag (BEGIN_MOVE ... END_MOVE) it is applied without an undo step
   * each time, and the drag is one step.
   */
  | { type: "SET_EDGE_WAYPOINTS"; ref: string; waypoints: readonly Point[] }
  /**
   * Move one end of an existing edge to another port. The edge keeps its
   * identifier, template, hydraulic properties and bends (invariant 2): it is
   * the same channel, attached somewhere else.
   */
  | { type: "RECONNECT_EDGE"; ref: string; end: "from" | "to"; port: string }
  /**
   * A component's display label. Not its identifier: renaming that is an
   * explicit, tracked server operation (invariant 2). Presentation, so allowed
   * on locked block members. An empty label shows the identifier instead.
   */
  | { type: "SET_LABEL"; ref: string; label: string }
  /**
   * Which side of the symbol the selected components' labels sit on. `side`
   * omitted: each moves to the next side, clockwise from below. Presentation,
   * so allowed on locked block members; one undo step.
   */
  | { type: "SET_LABEL_SIDE"; side?: LabelSide; refs?: readonly string[] }
  /**
   * Change a component to another template of the same role, in place: same
   * identifier, label, position and connections; parameters the new template
   * declares are kept, the rest dropped and said so (checkTemplateSwap).
   */
  | { type: "SET_NODE_TEMPLATE"; ref: string; templateVersionId: string }
  /**
   * Whether a port is an external cartridge interface (D15). Locked in blocks.
   * Marking one external opens it if it was dead-ended: it cannot be both.
   */
  | { type: "SET_PORT_EXTERNAL"; ref: string; external: boolean }
  /**
   * Close a port on purpose, or open it again. Refused while a line uses it,
   * and on locked block members. Dead-ending an external port makes it internal.
   */
  | { type: "SET_PORT_DEAD_END"; ref: string; deadEnd: boolean }
  /**
   * Write a note at `at` (its top-left corner), selected. With `anchor`, it
   * moves with that component. Empty text writes nothing.
   */
  | { type: "ADD_NOTE"; at: Point; text: string; anchor?: string | null; size?: NoteSizeValue }
  /**
   * Rewrite a note, and keep the size its editor was left at. Emptied, the
   * note is deleted: there are no blank notes.
   */
  | { type: "SET_NOTE_TEXT"; ref: string; text: string; size?: NoteSizeValue }
  /**
   * Resize a note's card; its text wraps to the new width. During a drag
   * (BEGIN_MOVE ... END_MOVE) applied without an undo step each time.
   */
  | { type: "SET_NOTE_SIZE"; ref: string; size: NoteSizeValue }
  /** Attach a note to a component, so it moves with it, or detach it (null). */
  | { type: "SET_NOTE_ANCHOR"; ref: string; anchor: string | null }
  /**
   * Put a part drawn in line between two tubes onto one unbroken tube (D40):
   * the tubes join, the part sits where they met. Tubing sets only.
   */
  | { type: "MOUNT_ON_LINE"; ref: string }
  /** Where a mounted part sits on its tube: metres from the tube's own start (D40). */
  | { type: "SET_MOUNT_DISTANCE"; ref: string; distance: number }
  | { type: "COPY_SELECTION" }
  /** Copy, then delete what was copied. Refused as deletion is (locked members). */
  | { type: "CUT_SELECTION" }
  /**
   * Paste the clipboard as new elements with fresh identifiers, selected.
   * At `at` (the copy's centre lands there), or offset from the original,
   * further each time, so repeated pastes do not stack.
   */
  | { type: "PASTE"; at?: Point }
  | { type: "UNDO" }
  | { type: "REDO" };

/** How many undo steps are kept. Snapshots are cheap at D19's ~80 nodes. */
export const UNDO_LIMIT = 200;

export const DEFAULT_GRID: GridSettings = { spacing: 16, snap: true, visible: true };

const EMPTY_LIBRARY: SceneLibrary = { templates: {}, definitions: {} };

export function createEditorState(
  document: DesignDocument = {},
  options: {
    revision?: number | null;
    library?: SceneLibrary;
    grid?: Partial<GridSettings>;
    reserved?: Iterable<string>;
    designKind?: DesignKind;
    templateEntities?: Readonly<Record<string, string>>;
    userDefaults?: PlacementDefaults;
  } = {},
): EditorState {
  const kind = options.designKind ?? "microfluidic";
  return {
    document,
    designKind: kind,
    revision: options.revision ?? null,
    reserved: new Set(options.reserved ?? []),
    savedDocument: document,
    library: options.library ?? EMPTY_LIBRARY,
    selection: new Set(),
    tool: "select",
    connectorTemplateId: options.library ? defaultConnector(options.library, kind) : null,
    junctionTemplateId: options.library ? defaultJunction(options.library, kind) : null,
    airLineTemplateId: options.library ? defaultAirLine(options.library, kind) : null,
    templateEntities: options.templateEntities ?? {},
    userDefaults: options.userDefaults ?? NO_DEFAULTS,
    drag: null,
    grid: { ...DEFAULT_GRID, ...options.grid },
    collapsed: new Set(),
    annotations: ALL_ANNOTATIONS,
    clipboard: null,
    viewport: DEFAULT_VIEWPORT,
    undo: [],
    redo: [],
    notice: null,
  };
}

/** Templates a design of this kind may contain (D34). */
function templatesFor(library: SceneLibrary, kind: DesignKind) {
  return Object.entries(library.templates).filter(([, template]) => servesKind(template, kind));
}

/** The first plain channel template, so the connector tool works out of the box. */
export function defaultConnector(library: SceneLibrary, kind: DesignKind = "microfluidic"): string | null {
  const entries = templatesFor(library, kind);
  const channel =
    entries.find(([, t]) => t.element === "edge" && t.role === "channel") ??
    entries.find(([, t]) => t.element === "edge");
  return channel ? channel[0] : null;
}

/** An air line template, if the library has one (D30). */
export function defaultAirLine(library: SceneLibrary, kind: DesignKind = "microfluidic"): string | null {
  return templatesFor(library, kind).find(([, t]) => t.element === "edge" && t.role === "air_line")?.[0] ?? null;
}

/**
 * The junction a branch is made with: a T if the library has one, else any
 * channel junction with at least three fluidic ports.
 */
export function defaultJunction(library: SceneLibrary, kind: DesignKind = "microfluidic"): string | null {
  const junctions = templatesFor(library, kind).filter(
    ([, t]) =>
      (t.element ?? "node") === "node" &&
      t.role === "channel_junction" &&
      (t.ports ?? []).filter((port) => (port.kind ?? "fluidic") === "fluidic").length >= 3,
  );
  // The rail point (D32) first; a T from an older library second.
  const tee =
    junctions.find(([, t]) => t.symbol.glyph === "junction") ??
    junctions.find(([, t]) => t.symbol.glyph === "t_junction") ??
    junctions[0];
  return tee ? tee[0] : null;
}

export function hasUnsavedChanges(state: EditorState): boolean {
  return state.document !== state.savedDocument;
}

export function canUndo(state: EditorState): boolean {
  return state.undo.length > 0;
}

export function canRedo(state: EditorState): boolean {
  return state.redo.length > 0;
}
